// ════════════════════════════════════════════════════════════════
// QR CODE — Display and Scanner components
// ════════════════════════════════════════════════════════════════

import { useEffect, useState, useRef } from 'react';
import QRCode from 'qrcode';
import jsQR from 'jsqr';

// ── QR Code Display ──
export function QRCodeDisplay({ value, size = 256 }: { value: string; size?: number }) {
  const [dataUrl, setDataUrl] = useState('');

  useEffect(() => {
    if (!value) return;
    QRCode.toDataURL(value, {
      width: size,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then(setDataUrl)
      .catch(() => setDataUrl(''));
  }, [value, size]);

  if (!dataUrl) return null;

  return (
    <div className="bg-white rounded-2xl p-4 inline-block">
      <img src={dataUrl} alt="QR Code" width={size} height={size} className="rounded-lg" />
    </div>
  );
}

// ── QR Code Scanner ──
interface QRScannerProps {
  onScan: (result: string) => void;
  onClose: () => void;
}

export function QRScanner({ onScan, onClose }: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let stream: MediaStream | null = null;
    let animationId: number;

    const startScanner = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const tick = () => {
          if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            ctx.drawImage(videoRef.current, 0, 0);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: 'dontInvert',
            });

            if (code && code.data) {
              onScan(code.data);
              return;
            }
          }
          animationId = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        setError('Camera access denied. Please allow camera access to scan QR codes.');
      }
    };

    startScanner();

    return () => {
      cancelAnimationFrame(animationId);
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, [onScan]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center p-4">
      <div className="relative w-full max-w-sm aspect-square">
        <video
          ref={videoRef}
          className="w-full h-full object-cover rounded-2xl"
          playsInline
          muted
        />
        {/* Scan overlay */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-8 border-2 border-accent-500 rounded-2xl" />
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-400 mt-4 text-center max-w-xs">{error}</p>
      )}

      <p className="text-sm text-vault-400 mt-4 text-center">
        Point camera at a public crypto address QR code
      </p>

      <button onClick={onClose} className="vault-button-secondary mt-6">
        Cancel
      </button>
    </div>
  );
}
